/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */

import React, { useEffect, useState } from 'react';
import { useLanguage } from '@/providers/LanguageProvider';
import { actionTypes } from '@/reducers/actionTypes/actionTypes';
import { hasDependencyChanges } from '@/utils/componentUtils';
import { handleError } from '@/utils/handelErrorMessage';
import { zodResolver } from '@hookform/resolvers/zod';
import componentsArr from 'data/components-library.json';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Component } from 'types/api-data';
import { IModalHeaderProp } from 'types/modal';
import { IDependencies } from 'types/services';
import { z } from 'zod';

import { cn } from '@/lib/utils';
import { useComponents } from '@/hooks/useComponents';
import { useExperienceScopes } from '@/hooks/useExperienceScope';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
} from '@/components/ui/dialog';
import { Form } from '@/components/ui/form';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { CustomButton } from '@/components/common';
import { ModalHeader } from '@/components/modal';
import { FullScreenSpinner } from '@/components/spinner/full-screen-spinner';

import { ArchiveComponent } from '../archive-component';
import { DeleteComponent } from '../delete-component';
import { GeneralTab } from './general-tab';
import { useComponentModalData } from './useComponentModalData';

export const ComponentDetailModal = ({
  component,
  isOpen,
  onClose,
  typeName,
  onSuccess,
}: {
  component: Component;
  isOpen: boolean;
  onClose: () => void;
  typeName?: string;
  onSuccess?: () => void;
}) => {
  const [loadingText, setLoadingText] = useState('');
  const [updateComponentLoading, setUpdateComponentLoading] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [restoringComponent, setRestoringComponent] = useState(false);
  const [isArchiveDialogOpen, setIsArchiveDialogOpen] = useState(false);
  const [selectedService, setSelectedService] = useState<any | null>(null);
  const [disassociatingAppsLoading, setDisassociatingAppsLoading] =
    useState(false);
  const {
    dispatch,
    state: { deletingComponent, archivingComponent },
    componentsActions,
  } = useComponents();
  const { messages } = useLanguage();
  const generalIntl = messages?.General;
  const compIntl = messages?.ComponentsPage;

  const dialogStrings = messages?.ComponentsPage;

  const {
    dependencies,
    dependencyLoading,
    selectedApp,
    setSelectedApp,
    selectedWorkflows,
    setSelectedWorkflows,
  } = useComponentModalData(component?.component_name, component?.type);

  useEffect(() => {
    if (component?.served_by) {
      setSelectedService({
        component_name: component.served_by.component_name,
        display_name: component.served_by.display_name,
        type: component.served_by.type,
      });
    }
  }, [component?.served_by]);

  const FormSchema = z.object({
    display_name: z.string().min(2, {
      message: generalIntl?.name_validation,
    }),
  });

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      display_name: component?.display_name || '',
    },
  });

  const { formState, reset } = form;
  const { isDirty } = formState;

  const showDeleteIcon =
    component?.status === 'archived' && (component?.apps ?? []).length === 0;

  const handleDeleteOrArchive = () => {
    if (component?.status === 'active') {
      setIsArchiveDialogOpen(true);
    } else if (
      component?.status === 'archived' &&
      (component?.apps ?? []).length === 0
    ) {
      setIsDeleteDialogOpen(true);
    }
  };

  const modalProps: IModalHeaderProp = {
    typeHead: component?.type,
    type: component?.type,
    displayTitle: component.display_name,
    status: component?.status,
    appsAssociated: component?.apps?.length ?? 0,
    idValue: component?.component_name,
    showDeleteIcon: showDeleteIcon,
    handleDeleteOrArchive: handleDeleteOrArchive,
    typeName,
    autoDiscovered: component?.auto_discovered,
    isComponent: true,
  };

  const experienceScopes = useExperienceScopes(
    component,
    undefined,
    undefined,
    componentsArr
  );

  const updateComponent = async (data: z.infer<typeof FormSchema>) => {
    // Determine current component type
    const currentType =
      experienceScopes.experienceTypeSelected?.id || component?.type;
    // Check if custom experience type
    const isCustomType =
      currentType?.startsWith('experience.') &&
      (currentType === 'experience.custom' ||
        experienceScopes.customScopes.length > 0);

    // Handle custom scopes
    const customScopeProperties = experienceScopes.customScopes.reduce(
      (acc, { name, value }) => {
        if (name) acc[name] = value;
        return acc;
      },
      {} as Record<string, string>
    );

    const properties = {
      ...component?.properties,
      // Only include scopes if it is experience type component
      ...(currentType?.startsWith('experience.') && {
        scopes: {
          // Include predefined scopes if any non-custom types are selected
          ...(experienceScopes.selectedExperienceTypes.some(
            (t) => t.id !== 'experience.custom'
          )
            ? experienceScopes.selectedScopes
            : {}),
          ...customScopeProperties,
        },
      }),
    };

    const body = {
      ...component,
      display_name: data?.display_name || component?.display_name,
      type: isCustomType ? 'experience.custom' : currentType,
      properties,
    };

    try {
      await componentsActions.updateComponent(component?.component_name, body);
      toast.success(compIntl?.component_updated_successfully);
      return false;
    } catch (error) {
      handleError(error, compIntl?.error_updating_component);
      return true;
    }
  };
  const checkChangesInComp = hasDependencyChanges(
    selectedApp,
    selectedService,
    dependencies,
    selectedWorkflows
  );

  const updateDependencies = async (errorInComponent: boolean) => {
    if (checkChangesInComp) {
      setLoadingText(
        generalIntl?.updating_dependencies ?? 'Updating dependencies'
      );
      const filterOtherDependencies = dependencies.filter(
        (item: IDependencies) =>
          item.dependency_type !== 'comprises' &&
          item.dependency_type !== 'served_by'
      );

      const appDependencies = selectedApp?.map((app) => ({
        source_name: app.name,
        dependency_type: 'comprises',
      }));

      // Start with other dependencies and app dependencies
      let updatedDependencies: IDependencies[] = [
        ...filterOtherDependencies,
        ...(appDependencies || []),
      ];

      // Add service dependency if selectedService exists
      if (selectedService) {
        const serviceDependency = {
          target_name: selectedService.component_name,
          dependency_type: 'served_by',
        };
        updatedDependencies = [...updatedDependencies, serviceDependency];
      }

      if (selectedWorkflows) {
        const workflowDependencies: IDependencies[] = selectedWorkflows.map(
          (workflow) => ({
            source_name: workflow.component_name,
            dependency_type: 'consumes',
          })
        );
        // remove consumes from updatedDependencies
        const notWorkflowDeps = updatedDependencies.filter(
          (item: IDependencies) => item.dependency_type !== 'consumes'
        );
        updatedDependencies = [...notWorkflowDeps, ...workflowDependencies];
      }

      try {
        await componentsActions.updateComponentDependencies(
          component?.component_name,
          updatedDependencies
        );

        // Create apps array for dispatch
        const appsForDispatch = selectedApp?.map((app) => ({
          name: app.name,
          display_name: app.display_name,
        }));

        dispatch({
          type: actionTypes.ASSOCIABLE_APPS,
          payload: {
            component_name: component?.component_name,
            apps: appsForDispatch,
            served_by: selectedService
              ? {
                  component_name: selectedService.component_name,
                  display_name: selectedService.display_name,
                  type: selectedService.type,
                }
              : undefined,
          },
        });

        toast.success(generalIntl?.updated_dependencies_successfully);

        if (!errorInComponent) {
          setSelectedApp([]);
          setSelectedService(null);
          reset();
          onClose();
          onSuccess?.();
        }
      } catch (error) {
        handleError(error, generalIntl?.failed_to_update_dependencies);
      }
    } else {
      if (!errorInComponent) {
        reset();
        setSelectedApp([]);
        setSelectedService(null);
        onClose();
        onSuccess?.();
      }
    }
  };

  const onSubmit = async (data: z.infer<typeof FormSchema>) => {
    let errorInComponent = false;

    try {
      setUpdateComponentLoading(true);
      setLoadingText(generalIntl?.updating_component ?? 'Updating component');

      const currentScopes = component?.type.startsWith('experience.')
        ? JSON.stringify(experienceScopes.mergeAllScopes())
        : null;
      const originalScopes = JSON.stringify(component.properties?.scopes || {});

      const isAnythingDirty =
        isDirty ||
        (component?.type.startsWith('experience.') &&
          (experienceScopes.experienceTypeSelected?.id !== component?.type ||
            currentScopes !== originalScopes));

      if (isAnythingDirty) {
        errorInComponent = await updateComponent(data);
      }

      await updateDependencies(errorInComponent);
    } catch (error) {
      console.log(error);
    } finally {
      setUpdateComponentLoading(false);
      setLoadingText('');
    }
  };

  const onRestore = async () => {
    try {
      setRestoringComponent(true);
      const success = await componentsActions.restoreComponent(component);
      if (success) {
        onClose();
        onSuccess?.();
        toast.success(dialogStrings?.component_restored_successfully_msg);
      }
    } catch (error) {
      handleError(error, dialogStrings?.error_component_restored_msg);
      console.error('Error restoring component:', error);
    } finally {
      setRestoringComponent(false);
    }
  };

  const spinners = [
    { condition: updateComponentLoading, text: loadingText },
    {
      condition: deletingComponent,
      text: dialogStrings?.deleting_component_msg,
    },
    {
      condition: restoringComponent,
      text: dialogStrings?.restoring_component_msg,
    },
    {
      condition: archivingComponent,
      text: dialogStrings?.archiving_component_msg,
    },
    {
      condition: disassociatingAppsLoading,
      text: messages?.ComponentsPage?.disassociating_apps,
    },
  ];

  return (
    <>
      {spinners.map(
        ({ condition, text }, index) =>
          condition && <FullScreenSpinner key={index} text={text as string} />
      )}
      <Dialog open={isOpen} onOpenChange={() => onClose()}>
        <DialogContent
          className={`flex min-w-[800px] flex-col gap-0
            border-link-active bg-gradient-sky-dark p-[30px]`}
          aria-describedby={undefined}
          onInteractOutside={(e) => {
            e.preventDefault();
          }}
        >
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <ModalHeader modalProps={modalProps}>
                <Tabs defaultValue="general">
                  <TabsList className="flex h-12 w-full justify-start gap-8 rounded-none border-b border-line-primary bg-transparent p-0 ">
                    <TabsTrigger
                      defaultValue="general"
                      value="general"
                      className={cn(
                        'rounded-none border-b-2 border-b-transparent px-0 py-0 pb-[22px] pt-2 text-base font-medium leading-none text-link-active hover:border-b-link-active data-[state=active]:border-b-body-primary data-[state=active]:bg-transparent data-[state=active]:text-body-primary'
                      )}
                    >
                      {generalIntl?.general}
                    </TabsTrigger>

                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="cursor-not-allowed py-0 pb-6 pt-2 text-h4 font-medium leading-none text-form-input-disabled">
                            <span className="mr-1">
                              {generalIntl?.additional_field}
                            </span>
                            <span>({generalIntl?.wip})</span>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          {generalIntl?.additional_field_coming_soon_msg}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </TabsList>
                  <ScrollArea className="h-[640px]">
                    <TabsContent value="general" className="mt-6 space-y-4">
                      <GeneralTab
                        component={component}
                        form={form}
                        selectedApp={selectedApp}
                        setSelectedApp={setSelectedApp}
                        selectedWorkflows={selectedWorkflows}
                        setSelectedWorkflows={setSelectedWorkflows}
                        selectedService={selectedService}
                        setSelectedService={setSelectedService}
                        dependencyLoading={dependencyLoading}
                        isArchived={component?.status === 'archived'}
                        experienceScopes={experienceScopes}
                      />
                    </TabsContent>
                  </ScrollArea>
                </Tabs>
              </ModalHeader>

              <DialogFooter className="mt-8 space-x-2 border-t border-line-primary pt-5 sm:justify-start">
                {component?.status === 'active' ? (
                  <CustomButton
                    customSize="regular"
                    customVariant="primary"
                    disabled={
                      updateComponentLoading || experienceScopes.areFactsEmpty()
                    }
                  >
                    {generalIntl?.save}
                  </CustomButton>
                ) : (
                  <CustomButton
                    type="button"
                    customSize="regular"
                    customVariant="primary"
                    onClick={onRestore}
                    disabled={restoringComponent}
                  >
                    {restoringComponent
                      ? `${messages?.General?.restoring}`
                      : `${messages?.General?.restore}`}
                  </CustomButton>
                )}

                <DialogClose asChild>
                  <CustomButton customVariant="secondary" customSize="regular">
                    {generalIntl?.cancel}
                  </CustomButton>
                </DialogClose>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
      {isDeleteDialogOpen && (
        <DeleteComponent
          isOpen={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          componentName={component.component_name}
          onSuccess={onSuccess}
        />
      )}
      {isArchiveDialogOpen && (
        <ArchiveComponent
          isOpen={isArchiveDialogOpen}
          onClose={() => setIsArchiveDialogOpen(false)}
          component={component}
          onSuccess={onSuccess}
          onDisassociatingApps={setDisassociatingAppsLoading}
        />
      )}
    </>
  );
};
